<template>
    <div>
        <div
            v-if="!isReady || isLoading"
            class="grid grid-cols-1 gap-4 md:grid-cols-6"
        >
            <!-- Card Number -->
            <div class="md:col-span-4">
                <div class="mb-1">Card number</div>
                <prime-skeleton class="min-h-[36px]" />
            </div>

            <!-- Expiration Date -->
            <div class="md:col-span-1">
                <div class="mb-1">Exp. date</div>
                <prime-skeleton class="min-h-[36px]" />
            </div>

            <!-- CVV -->
            <div class="md:col-span-1">
                <div class="mb-1">CVV</div>
                <prime-skeleton class="min-h-[36px]" />
            </div>
        </div>

        <windcave-hosted-fields
            v-if="isReady && data !== undefined"
            ref="windcaveHostedFieldsRef"
            :key="uniqueQueryId"
            :payment-source-id="props.paymentSourceId"
            :payment-source-data="data"
            @payment-method="$emit('paymentMethod', $event)"
            @transaction-error="handleTransactionError"
            @validation-error="handleValidationError"
        />

        <div
            v-if="transactionError || validationError || isError"
            class="mt-4 flex items-center justify-between gap-4"
        >
            <div class="font-bold text-danger-600">
                {{ transactionError || validationError || isError || '&nbsp;' }}
            </div>

            <prime-button
                text
                class="!p-1"
                label="Refresh Payment Form"
                :loading="isProcessing"
                @click="handleRefreshPaymentForm"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, toRef } from 'vue'
import { type TokenPaymentMethod } from '~/components/Checkout/Sections/Payment/payment-method'
import { useWindcaveScripts } from '~/components/Payments/use-windcave-scripts'
import WindcaveHostedFields from '~/components/Payments/WindcaveHostedFields.vue'
import { useQuery } from '@tanstack/vue-query'
import PrimeSkeleton from 'primevue/skeleton'
import { randomString } from '~/composables/support/util'
import PrimeButton from 'primevue/button'
import {
    useWindcaveQuery,
    type WindcaveSessionInputData
} from '~/components/Payments/use-windcave-query'
import type { PaymentMethodInput } from '~/components/Checkout/Sections/use-payment-method-input'
import type { Decimal } from '~/composables/support/decimal'

const props = defineProps<{
    paymentSourceId: string
    amount?: Decimal
    input: PaymentMethodInput
    refreshButtonAboveForm?: boolean
    isProcessing?: boolean
}>()

defineEmits<{
    (e: 'paymentMethod', payload: TokenPaymentMethod | undefined): void
}>()

const { loadWindcaveHostedScripts } = useWindcaveScripts()
const isReady = ref(false)
loadWindcaveHostedScripts().then(() => {
    isReady.value = true
})

const transactionError = ref<string>()
const validationError = ref<string>()

const windcaveSessionInput = computed<WindcaveSessionInputData>(() => {
    // Amount greater than zero is a purchase session
    if (props.amount !== undefined) {
        return {
            type: 'WindcavePurchaseSessionInputData',
            amount: props.amount.toFixed(2),
            firstName: props.input.customerInformation.firstName,
            lastName: props.input.customerInformation.lastName,
            phone: props.input.customerInformation.phone,
            email: props.input.customerInformation.email,
            postalCode: props.input.addressInformation.postal
        }
    } else {
        return {
            type: 'WindcaveTokenizeSessionInputData',
            firstName: props.input.customerInformation.firstName,
            lastName: props.input.customerInformation.lastName,
            phone: props.input.customerInformation.phone,
            email: props.input.customerInformation.email,
            postalCode: props.input.addressInformation.postal
        }
    }
})

const uniqueQueryId = ref(randomString())
const { queryWindcaveSession } = useWindcaveQuery()
// We use vue-query here so we get retries on the initial load, but we don't
// want to cache the data
const { data, isLoading, isError } = useQuery({
    // Never consider the data stale
    staleTime: Infinity,
    // Keep data in cache forever
    gcTime: Infinity,
    // Disable automatic refetches
    refetchOnMount: false, // won't refetch when component mounts
    refetchOnWindowFocus: false, // won't refetch when window refocuses
    refetchOnReconnect: false, // won't refetch when reconnecting
    refetchInterval: false, // disables polling
    queryKey: [
        'WindcaveSetupFormQuery',
        uniqueQueryId,
        toRef(props, 'paymentSourceId'),
        windcaveSessionInput
    ],
    queryFn: async () => {
        return queryWindcaveSession(
            props.paymentSourceId,
            windcaveSessionInput.value
        )
    }
})

function handleTransactionError(message: string) {
    transactionError.value = message
    uniqueQueryId.value = randomString()
}

function handleValidationError(message: string) {
    validationError.value = message
}

const windcaveHostedFieldsRef = ref<InstanceType<typeof WindcaveHostedFields>>()
async function submitPaymentMethod() {
    transactionError.value = undefined
    validationError.value = undefined
    if (windcaveHostedFieldsRef.value !== undefined) {
        await windcaveHostedFieldsRef.value.submitPaymentMethod()
    }
}

function handleRefreshPaymentForm() {
    transactionError.value = undefined
    validationError.value = undefined
    uniqueQueryId.value = randomString()
}

defineExpose({
    submitPaymentMethod
})
</script>
